// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — colour system + WCAG contrast helper (principle #22). The play surface is the dark
// yard ground; every piece that carries meaning is drawn on it and must clear the 3:1 graphic
// floor (WCAG 2.1 §1.4.11). Those pieces are: the RAIL, the eight WAGON liveries, the LOCO,
// the DEPARTURE-road marker, the legal-target highlight and the goal glow.
//
// Colour is NEVER the only cue. A wagon carries its LETTER in dark ink (that is the ground
// truth an achromatic player reads); the loco is a different SHAPE with a cab and a chimney;
// the departure road is flagged with a signal and a label; a legal tap target is outlined, not
// merely tinted. The eight liveries are drawn from an Okabe-Ito-derived set, filtered so that
// every one of them clears 3:1 against BOTH the yard ground and the darker ballast strip —
// which is why the classic dark blue (#0072B2, 2.7:1 here) is absent: on a ground this dark it
// is a hole, not a wagon.

export const PALETTE = {
  // shell + panels
  bg: '#141a1d',
  bgDeep: '#0e1315',
  panel: '#1e282d',
  panelDeep: '#161e22',
  edge: '#33434b',

  // the play surface and the strip the rails sit on
  yard: '#1b2429', // the board — wagons and rails are drawn on this
  ballast: '#141b1f', // the darker road each track occupies
  rail: '#9fb3bd', // steel

  // text
  text: '#eef4f7',
  textMuted: '#a3b6c0',
  ink: '#12181b', // the dark letter drawn ON a wagon

  // the loco — a distinct shape as well as a distinct colour
  loco: '#f2a63b',

  // roles on the play surface
  departure: '#7be0a0', // the departure road's flag + label
  target: '#ffd166', // outline of a track/wagon you may tap right now
  ghost: '#63808e', // the order slip's not-yet-placed silhouettes
  good: '#7be0a0',
  bad: '#ff7a6b',
} as const;

/**
 * The eight wagon liveries, indexed by wagon id. Each is paired with a letter (A..H) that is
 * drawn on the wagon in `ink` — the letter, not the colour, is what the rules are stated in.
 */
export const LIVERY: readonly string[] = [
  '#56b4e9', // A sky
  '#f0e442', // B yellow
  '#cc79a7', // C pink
  '#12a97b', // D green
  '#b39dff', // E lavender
  '#dce6ec', // F silver
  '#ff8a65', // G coral
  '#5fd9c8', // H mint
];

export const LETTERS = 'ABCDEFGH';

/** The letter for a wagon id. Ids beyond the livery set wrap, but no mode ever needs that. */
export function letterOf(id: number): string {
  return LETTERS[id % LETTERS.length];
}

export function liveryOf(id: number): string {
  return LIVERY[id % LIVERY.length];
}

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
